import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getWarehouses, createWarehouse, updateWarehouse, getWarehouseHandovers, deleteWarehouse, getItemTypes, createItemType, deleteItemType, createInventoryItem, createStockBatch, downloadInventoryBulkTemplate, bulkUploadInventory } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Plus, Trash2, Warehouse as WarehouseIcon, Tag, PackagePlus, Boxes, Upload, Download, Pencil, History as HistoryIcon } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_OPTIONS = [
  { value: 'qr_standee', label: 'QR Standee' },
  { value: 'sound_box', label: 'Sound Box' },
  { value: 'pos_machine', label: 'POS Machine' },
  { value: 'paper_qr', label: 'Paper QR' },
  { value: 'other', label: 'Other' },
];

const WarehousesAndCatalog = ({ user, setUser }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [whForm, setWhForm] = useState({ name: '', location: '', state: '', pincode: '', incharge_name: '', incharge_phone: '' });
  const [itForm, setItForm] = useState({ name: '', category: 'sound_box', tracking_mode: 'serialized', sku: '', vendor: '', cost_price: 0, description: '' });
  const [serialForm, setSerialForm] = useState({ item_type_id: '', serial_number: '', warehouse_id: '', cost_price: 0, notes: '' });
  const [batchForm, setBatchForm] = useState({ item_type_id: '', warehouse_id: '', quantity: 100, batch_code: '', cost_per_unit: 0, notes: '' });
  const [whOpen, setWhOpen] = useState(false);
  const [editWhOpen, setEditWhOpen] = useState(false);
  const [editWhForm, setEditWhForm] = useState(null);  // {id, name, ..., original_incharge_name, original_incharge_phone, handover_notes}
  const [editWhHistory, setEditWhHistory] = useState([]);
  const [editWhSaving, setEditWhSaving] = useState(false);
  const [itOpen, setItOpen] = useState(false);
  const [serialOpen, setSerialOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);

  // Bulk upload state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await downloadInventoryBulkTemplate();
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'inventory_bulk_template.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const submitBulk = async () => {
    if (!bulkFile) { toast.error('Please choose a file first'); return; }
    try {
      setBulkUploading(true);
      const fd = new FormData(); fd.append('file', bulkFile);
      const r = await bulkUploadInventory(fd);
      setBulkResult(r.data);
      const totalCreated = (r.data.created_serialized || 0) + (r.data.created_batches || 0);
      if (totalCreated > 0) toast.success(`Created ${r.data.created_serialized} serialized + ${r.data.created_batches} batches`);
      if (r.data.errors?.length > 0) toast.warning(`${r.data.errors.length} rows had errors`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBulkUploading(false); }
  };

  const loadAll = async () => {
    try {
      const [w, t] = await Promise.all([getWarehouses(), getItemTypes()]);
      setWarehouses(w.data); setItemTypes(t.data);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { loadAll(); }, []);

  const submitWh = async () => {
    try { await createWarehouse(whForm); toast.success('Warehouse added'); setWhOpen(false); loadAll();
      setWhForm({ name: '', location: '', state: '', pincode: '', incharge_name: '', incharge_phone: '' }); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitIt = async () => {
    try { await createItemType({ ...itForm, cost_price: parseFloat(itForm.cost_price) || 0 }); toast.success('Item type added'); setItOpen(false); loadAll();
      setItForm({ name: '', category: 'sound_box', tracking_mode: 'serialized', sku: '', vendor: '', cost_price: 0, description: '' }); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitSerial = async () => {
    try { await createInventoryItem({ ...serialForm, cost_price: parseFloat(serialForm.cost_price) || 0 }); toast.success('Item created'); setSerialOpen(false);
      setSerialForm({ item_type_id: '', serial_number: '', warehouse_id: '', cost_price: 0, notes: '' }); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitBatch = async () => {
    try { await createStockBatch({ ...batchForm, quantity: parseInt(batchForm.quantity) || 0, cost_per_unit: parseFloat(batchForm.cost_per_unit) || 0 }); toast.success('Batch created'); setBatchOpen(false);
      setBatchForm({ item_type_id: '', warehouse_id: '', quantity: 100, batch_code: '', cost_per_unit: 0, notes: '' }); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const delWh = async (id) => { if (!window.confirm('Delete warehouse?')) return;
    try { await deleteWarehouse(id); toast.success('Deleted'); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEditWh = async (w) => {
    setEditWhForm({
      id: w.id,
      name: w.name || '',
      location: w.location || '',
      state: w.state || '',
      pincode: w.pincode || '',
      incharge_name: w.incharge_name || '',
      incharge_phone: w.incharge_phone || '',
      original_incharge_name: w.incharge_name || '',
      original_incharge_phone: w.incharge_phone || '',
      handover_notes: '',
    });
    setEditWhOpen(true);
    setEditWhHistory([]);
    try {
      const r = await getWarehouseHandovers(w.id);
      setEditWhHistory(r.data.history || []);
    } catch { /* silent — history is a bonus */ }
  };

  const submitEditWh = async () => {
    if (!editWhForm) return;
    const inchargeChanged = (
      (editWhForm.incharge_name || '').trim() !== (editWhForm.original_incharge_name || '').trim()
      || (editWhForm.incharge_phone || '').trim() !== (editWhForm.original_incharge_phone || '').trim()
    );
    try {
      setEditWhSaving(true);
      const payload = {
        name: editWhForm.name,
        location: editWhForm.location,
        state: editWhForm.state,
        pincode: editWhForm.pincode,
        incharge_name: editWhForm.incharge_name,
        incharge_phone: editWhForm.incharge_phone,
      };
      if (inchargeChanged) {
        payload.handover_notes = editWhForm.handover_notes || null;
      }
      const r = await updateWarehouse(editWhForm.id, payload);
      if (r.data.handover_logged) {
        toast.success('Warehouse updated & handover logged');
      } else {
        toast.success('Warehouse updated');
      }
      setEditWhOpen(false);
      setEditWhForm(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setEditWhSaving(false);
    }
  };
  const delIt = async (id) => { if (!window.confirm('Deactivate item type?')) return;
    try { await deleteItemType(id); toast.success('Deactivated'); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const serializedTypes = itemTypes.filter(t => t.tracking_mode === 'serialized');
  const bulkTypes = itemTypes.filter(t => t.tracking_mode === 'bulk');

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Warehouses & Catalog</h1>
          <p className="text-sm text-slate-500">Manage warehouses, item types, and stock intake</p>
        </div>

        <Tabs defaultValue="warehouses">
          <TabsList>
            <TabsTrigger value="warehouses" data-testid="tab-warehouses"><WarehouseIcon className="h-4 w-4 mr-1" />Warehouses</TabsTrigger>
            <TabsTrigger value="catalog" data-testid="tab-catalog"><Tag className="h-4 w-4 mr-1" />Item Catalog</TabsTrigger>
            <TabsTrigger value="intake" data-testid="tab-intake"><PackagePlus className="h-4 w-4 mr-1" />Stock Intake</TabsTrigger>
          </TabsList>

          {/* Warehouses */}
          <TabsContent value="warehouses">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Warehouses ({warehouses.length})</CardTitle>
                <Dialog open={whOpen} onOpenChange={setWhOpen}>
                  <DialogTrigger asChild><Button size="sm" data-testid="add-warehouse-btn"><Plus className="h-4 w-4 mr-1" />Add Warehouse</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Warehouse</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Name *</Label><Input value={whForm.name} onChange={e => setWhForm({ ...whForm, name: e.target.value })} data-testid="wh-name" /></div>
                      <div><Label>Location *</Label><Input value={whForm.location} onChange={e => setWhForm({ ...whForm, location: e.target.value })} data-testid="wh-location" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>State</Label><Input value={whForm.state} onChange={e => setWhForm({ ...whForm, state: e.target.value })} /></div>
                        <div><Label>Pincode</Label><Input value={whForm.pincode} onChange={e => setWhForm({ ...whForm, pincode: e.target.value })} /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Incharge Name</Label><Input value={whForm.incharge_name} onChange={e => setWhForm({ ...whForm, incharge_name: e.target.value })} /></div>
                        <div><Label>Incharge Phone</Label><Input value={whForm.incharge_phone} onChange={e => setWhForm({ ...whForm, incharge_phone: e.target.value })} /></div>
                      </div>
                    </div>
                    <DialogFooter><Button onClick={submitWh} data-testid="wh-save">Save</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Location</th><th className="text-left p-2">State</th><th className="text-left p-2">Incharge</th><th className="p-2"></th></tr></thead>
                  <tbody>{warehouses.map(w => (
                    <tr key={w.id} className="border-b" data-testid={`wh-row-${w.id}`}>
                      <td className="p-2 font-medium">{w.name}</td>
                      <td className="p-2">{w.location}</td>
                      <td className="p-2">{w.state}</td>
                      <td className="p-2">
                        <div>{w.incharge_name} {w.incharge_phone && `(${w.incharge_phone})`}</div>
                        {(w.handover_history || []).length > 0 && (
                          <div className="text-[10px] text-slate-500 flex items-center gap-1">
                            <HistoryIcon className="h-3 w-3" />{w.handover_history.length} handover{w.handover_history.length > 1 ? 's' : ''}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => openEditWh(w)} data-testid={`edit-wh-${w.id}`}>
                          <Pencil className="h-3 w-3 text-indigo-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => delWh(w.id)} data-testid={`delete-wh-${w.id}`}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}{warehouses.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-500">No warehouses yet</td></tr>}</tbody>
                </table>
              </CardContent>
            </Card>

            {/* Edit Warehouse Dialog */}
            <Dialog open={editWhOpen} onOpenChange={(v) => { setEditWhOpen(v); if (!v) setEditWhForm(null); }}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-warehouse-dialog">
                <DialogHeader><DialogTitle>Edit Warehouse</DialogTitle></DialogHeader>
                {editWhForm && (
                  <div className="space-y-3">
                    <div><Label>Name *</Label><Input value={editWhForm.name} onChange={e => setEditWhForm({ ...editWhForm, name: e.target.value })} data-testid="edit-wh-name" /></div>
                    <div><Label>Location *</Label><Input value={editWhForm.location} onChange={e => setEditWhForm({ ...editWhForm, location: e.target.value })} data-testid="edit-wh-location" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>State</Label><Input value={editWhForm.state} onChange={e => setEditWhForm({ ...editWhForm, state: e.target.value })} /></div>
                      <div><Label>Pincode</Label><Input value={editWhForm.pincode} onChange={e => setEditWhForm({ ...editWhForm, pincode: e.target.value })} /></div>
                    </div>
                    <div className="border-t pt-3">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <HistoryIcon className="h-4 w-4 text-indigo-600" />
                        Warehouse Handover
                      </h4>
                      <p className="text-xs text-slate-500 mb-2">
                        Changing the Incharge below logs a handover entry with prev/new details and your name.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Incharge Name</Label><Input value={editWhForm.incharge_name} onChange={e => setEditWhForm({ ...editWhForm, incharge_name: e.target.value })} data-testid="edit-wh-incharge-name" /></div>
                        <div><Label>Incharge Phone</Label><Input value={editWhForm.incharge_phone} onChange={e => setEditWhForm({ ...editWhForm, incharge_phone: e.target.value })} data-testid="edit-wh-incharge-phone" /></div>
                      </div>
                      {(
                        (editWhForm.incharge_name || '').trim() !== (editWhForm.original_incharge_name || '').trim()
                        || (editWhForm.incharge_phone || '').trim() !== (editWhForm.original_incharge_phone || '').trim()
                      ) && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded" data-testid="handover-notes-block">
                          <Label className="text-xs">Handover Notes (optional)</Label>
                          <Input
                            value={editWhForm.handover_notes}
                            onChange={e => setEditWhForm({ ...editWhForm, handover_notes: e.target.value })}
                            placeholder="Reason / context for the handover"
                            data-testid="edit-wh-handover-notes"
                          />
                          <p className="text-[11px] text-amber-700 mt-1">
                            Handover will be logged:
                            <b> {editWhForm.original_incharge_name || '(none)'}</b>
                            {editWhForm.original_incharge_phone && ` (${editWhForm.original_incharge_phone})`}
                            {' → '}
                            <b>{editWhForm.incharge_name || '(none)'}</b>
                            {editWhForm.incharge_phone && ` (${editWhForm.incharge_phone})`}
                          </p>
                        </div>
                      )}
                    </div>

                    {editWhHistory.length > 0 && (
                      <div className="border-t pt-3">
                        <h4 className="text-sm font-medium mb-2">Handover History ({editWhHistory.length})</h4>
                        <div className="max-h-40 overflow-y-auto space-y-2" data-testid="handover-history-list">
                          {editWhHistory.map((h) => (
                            <div key={h.id} className="text-xs bg-slate-50 rounded p-2 border-l-2 border-indigo-400">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">
                                  {h.previous_incharge_name || '(none)'} → {h.new_incharge_name || '(none)'}
                                </span>
                                <span className="text-slate-500">{h.changed_at ? new Date(h.changed_at).toLocaleString() : ''}</span>
                              </div>
                              <div className="text-slate-600 mt-1">
                                Phone: {h.previous_incharge_phone || '-'} → {h.new_incharge_phone || '-'}
                              </div>
                              <div className="text-slate-500">
                                By: {h.handed_over_by_user_name || 'Unknown'}
                                {h.handover_notes && <span className="italic"> — {h.handover_notes}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditWhOpen(false)}>Cancel</Button>
                  <Button onClick={submitEditWh} disabled={editWhSaving} data-testid="edit-wh-save">
                    {editWhSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Catalog */}
          <TabsContent value="catalog">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Item Types ({itemTypes.length})</CardTitle>
                <Dialog open={itOpen} onOpenChange={setItOpen}>
                  <DialogTrigger asChild><Button size="sm" data-testid="add-item-type-btn"><Plus className="h-4 w-4 mr-1" />Add Item Type</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Item Type</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Name *</Label><Input value={itForm.name} onChange={e => setItForm({ ...itForm, name: e.target.value })} placeholder="e.g. Soundbox Model A" data-testid="it-name" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Category *</Label>
                          <Select value={itForm.category} onValueChange={v => setItForm({ ...itForm, category: v })}>
                            <SelectTrigger data-testid="it-category"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Tracking Mode *</Label>
                          <Select value={itForm.tracking_mode} onValueChange={v => setItForm({ ...itForm, tracking_mode: v })}>
                            <SelectTrigger data-testid="it-tracking"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="serialized">Serialized (per unit)</SelectItem>
                              <SelectItem value="bulk">Bulk (by qty)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>SKU</Label><Input value={itForm.sku} onChange={e => setItForm({ ...itForm, sku: e.target.value })} /></div>
                        <div><Label>Vendor</Label><Input value={itForm.vendor} onChange={e => setItForm({ ...itForm, vendor: e.target.value })} /></div>
                      </div>
                      <div><Label>Cost Price (₹)</Label><Input type="number" value={itForm.cost_price} onChange={e => setItForm({ ...itForm, cost_price: e.target.value })} /></div>
                      <div><Label>Description</Label><Input value={itForm.description} onChange={e => setItForm({ ...itForm, description: e.target.value })} /></div>
                    </div>
                    <DialogFooter><Button onClick={submitIt} data-testid="it-save">Save</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Category</th><th className="text-left p-2">Tracking</th><th className="text-left p-2">SKU</th><th className="text-right p-2">Cost</th><th className="p-2"></th></tr></thead>
                  <tbody>{itemTypes.map(t => (
                    <tr key={t.id} className="border-b"><td className="p-2 font-medium">{t.name}</td><td className="p-2 capitalize">{t.category.replace('_', ' ')}</td><td className="p-2"><span className={`px-2 py-0.5 text-xs rounded ${t.tracking_mode === 'serialized' ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>{t.tracking_mode}</span></td><td className="p-2">{t.sku || '-'}</td><td className="p-2 text-right">₹{t.cost_price || 0}</td><td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => delIt(t.id)}><Trash2 className="h-3 w-3 text-red-500" /></Button></td></tr>
                  ))}{itemTypes.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">No item types yet</td></tr>}</tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Intake */}
          <TabsContent value="intake">
            {/* Bulk upload banner */}
            <Card className="mb-4 border-indigo-200 bg-indigo-50/50">
              <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[260px]">
                  <p className="font-semibold text-slate-900 flex items-center gap-2"><Upload className="h-4 w-4 text-indigo-600" />Bulk Upload from Excel</p>
                  <p className="text-xs text-slate-600 mt-1">Recommended for large vendor shipments. Supports both serialized (per-serial) and bulk (qty-based) items in one file.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={downloadTemplate} data-testid="inv-download-template-btn"><Download className="h-4 w-4 mr-1" />Download Template</Button>
                  <Button onClick={() => { setBulkOpen(true); setBulkResult(null); setBulkFile(null); }} data-testid="inv-bulk-upload-btn"><Upload className="h-4 w-4 mr-1" />Bulk Upload</Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Serialized intake */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Boxes className="h-4 w-4" />Add Serialized Item</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label>Item Type</Label>
                    <Select value={serialForm.item_type_id} onValueChange={v => setSerialForm({ ...serialForm, item_type_id: v })}>
                      <SelectTrigger data-testid="serial-item-type"><SelectValue placeholder="Choose..." /></SelectTrigger>
                      <SelectContent>{serializedTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Serial Number *</Label><Input value={serialForm.serial_number} onChange={e => setSerialForm({ ...serialForm, serial_number: e.target.value })} placeholder="e.g. SB-2026-00001" data-testid="serial-number" /></div>
                  <div><Label>Warehouse</Label>
                    <Select value={serialForm.warehouse_id} onValueChange={v => setSerialForm({ ...serialForm, warehouse_id: v })}>
                      <SelectTrigger data-testid="serial-warehouse"><SelectValue placeholder="Choose..." /></SelectTrigger>
                      <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name} - {w.location}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Cost Price (₹)</Label><Input type="number" value={serialForm.cost_price} onChange={e => setSerialForm({ ...serialForm, cost_price: e.target.value })} /></div>
                  <div><Label>Notes</Label><Input value={serialForm.notes} onChange={e => setSerialForm({ ...serialForm, notes: e.target.value })} /></div>
                  <Button onClick={submitSerial} disabled={!serialForm.item_type_id || !serialForm.serial_number || !serialForm.warehouse_id} className="w-full" data-testid="serial-save">Receive Item</Button>
                </CardContent>
              </Card>

              {/* Bulk intake */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><PackagePlus className="h-4 w-4" />Add Bulk Batch</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label>Item Type</Label>
                    <Select value={batchForm.item_type_id} onValueChange={v => setBatchForm({ ...batchForm, item_type_id: v })}>
                      <SelectTrigger data-testid="batch-item-type"><SelectValue placeholder="Choose..." /></SelectTrigger>
                      <SelectContent>{bulkTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Warehouse</Label>
                    <Select value={batchForm.warehouse_id} onValueChange={v => setBatchForm({ ...batchForm, warehouse_id: v })}>
                      <SelectTrigger data-testid="batch-warehouse"><SelectValue placeholder="Choose..." /></SelectTrigger>
                      <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name} - {w.location}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Quantity *</Label><Input type="number" value={batchForm.quantity} onChange={e => setBatchForm({ ...batchForm, quantity: e.target.value })} data-testid="batch-quantity" /></div>
                  <div><Label>Batch Code</Label><Input value={batchForm.batch_code} onChange={e => setBatchForm({ ...batchForm, batch_code: e.target.value })} placeholder="optional" /></div>
                  <div><Label>Cost / Unit (₹)</Label><Input type="number" value={batchForm.cost_per_unit} onChange={e => setBatchForm({ ...batchForm, cost_per_unit: e.target.value })} /></div>
                  <div><Label>Notes</Label><Input value={batchForm.notes} onChange={e => setBatchForm({ ...batchForm, notes: e.target.value })} /></div>
                  <Button onClick={submitBatch} disabled={!batchForm.item_type_id || !batchForm.warehouse_id || !batchForm.quantity} className="w-full" data-testid="batch-save">Receive Batch</Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Inventory Intake</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="rounded-lg border bg-blue-50 border-blue-200 p-3 text-sm">
                <p className="font-medium text-blue-900">Step 1 — Download Template</p>
                <p className="text-xs text-blue-700 mt-1">4 sheets: Inventory Intake (sample data), Item Types, Warehouses, Instructions.</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={downloadTemplate} data-testid="bulk-download-template"><Download className="h-4 w-4 mr-1" />Download Template</Button>
              </div>

              <div className="rounded-lg border bg-slate-50 border-slate-200 p-3 text-sm">
                <p className="font-medium text-slate-900">Step 2 — Upload Filled File</p>
                <p className="text-xs text-slate-600 mt-1">Mode column = serialized or bulk. Item Type & Warehouse must match names from the reference sheets.</p>
                <Input type="file" accept=".xlsx,.xls" onChange={e => { setBulkFile(e.target.files[0]); setBulkResult(null); }} className="mt-2" data-testid="bulk-file-input" />
              </div>

              {bulkResult && (
                <div className="rounded-lg border p-3 text-sm">
                  <p className="font-semibold text-green-700">
                    ✓ Created {bulkResult.created_serialized || 0} serialized item(s) + {bulkResult.created_batches || 0} batch(es)
                  </p>
                  {bulkResult.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="font-semibold text-red-700 mb-1">{bulkResult.errors.length} error{bulkResult.errors.length === 1 ? '' : 's'}:</p>
                      <div className="max-h-40 overflow-y-auto bg-red-50 p-2 rounded text-xs">
                        {bulkResult.errors.map((e, i) => (
                          <div key={i} className="text-red-800">Row {e.row}: {e.error}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBulkOpen(false); if (bulkResult?.created_serialized || bulkResult?.created_batches) loadAll(); }}>Close</Button>
              <Button onClick={submitBulk} disabled={!bulkFile || bulkUploading} data-testid="bulk-upload-confirm">
                {bulkUploading ? 'Uploading...' : 'Upload & Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WarehousesAndCatalog;
